// 제작 현황 「캘린더 보기」 (2026-09-29 대표 확정 — docs/기획서_제작현황_캘린더뷰_2026-09-29.md)
// 표시 계층 전용: ProductionBoard가 파생한 행(웹 의뢰서만, _col·_due)과 기존 쓰기 핸들러를 props로 받아 그리기만 한다.
//  · 날짜 축 = 납기일(_due)만. 내원일·접수일 축 없음. 날짜 드래그 변경 없음(납기는 치과가 정한 값).
//  · 수집 케이스(parsedCases)는 이 컴포넌트에 들어오지 않는다(보드에서 걸러서 넘김). 납기 없는 의뢰서는 개수만 표시.
//  · 쓰기는 전부 보드 핸들러(moveStage/moveIntakeToConfirm/toggleCheck) 호출 — 신규 필드·rules·CF 변경 0.
//  · 환자명 마스킹 없음(대표 결정 4). 로그·콘솔에 환자명을 찍지 않는다.
import React, { useState, useEffect, useMemo, useRef } from 'react';
import ProcessDayModal from './ProcessDayModal';   // 그날 환자 모달 — 치과 ClinicProcessCalendar와 공용 (2026-09-29 대표 지시)

// ── 팔레트 (ProductionBoard.C와 동일 값 — 톤 일치) ──
const C = {
    plane: '#f8fafc',
    surface: '#ffffff',
    textPrimary: '#0f172a',
    textSecondary: '#334155',
    muted: '#64748b',
    border: '#e2e8f0',
    critical: '#d03b3b',
    good: '#0ca30c',
    cat: ['#6366f1', '#eb6834', '#1baf7a', '#eda100'],
};

// 공정 배지 색 — 보드 열 순서(접수/확인/디자인완료/밀링완료)에 맞춘 고정 색. 완료(done — 구 shipping·status completed·서버 자동 완료 포함)는 회색.
//  2026-09-29 4단계: 단계 이동 버튼은 접수→확인→디자인완료→밀링완료까지(nextColOf가 없으면 다음 버튼 미표시). 완료는 납기 자정 서버 자동.
//  2026-10-12 3단계(기공소·치과 모드 공통): 열 키 intake/confirm/design/packed(포장 완료). 'mill'은 더 이상 오지 않는다(색·순서 표는 호환용으로 남김).
//   접수(intake) 행은 단계가 아니므로 배지를 달지 않는다(칩 없음 — 계약_공정3단계 §2 ⑥).
const COL_ORDER = ['intake', 'confirm', 'design', 'mill', 'packed', 'done'];
const COL_STYLE = {
    intake: { fg: C.muted, bg: 'rgba(100,116,139,0.10)', bd: 'rgba(100,116,139,0.30)' },
    confirm: { fg: '#6366f1', bg: 'rgba(99,102,241,0.08)', bd: 'rgba(99,102,241,0.30)' },
    design: { fg: '#7c3aed', bg: 'rgba(124,58,237,0.08)', bd: 'rgba(124,58,237,0.35)' },
    mill: { fg: '#0f766e', bg: 'rgba(15,118,110,0.08)', bd: 'rgba(15,118,110,0.35)' },
    packed: { fg: '#0f766e', bg: 'rgba(15,118,110,0.08)', bd: 'rgba(15,118,110,0.35)' },   // 포장 완료 — 종전 마지막 열(밀링완료) 색 승계
    done: { fg: C.muted, bg: '#f1f5f9', bd: C.border },
};
// 가공·3D 체크가 붙는 열(보드 CHECK_COLS와 동일)
const CHECK_COLS = ['design', 'mill'];
const MOBILE_MAX_PX = 640;   // 이 폭 이하 = 주간을 날짜 헤더+세로 목록으로 전환 (기획서 문제점 12)
const WEEKDAY = ['월', '화', '수', '목', '금', '토', '일'];

// ── 날짜 유틸 (로컬 시각 기준) ──
const startOfDay = (d) => { const x = new Date(d.getTime()); x.setHours(0, 0, 0, 0); return x; };
const addDays = (d, n) => { const x = new Date(d.getTime()); x.setDate(x.getDate() + n); return x; };
const dayKey = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
// 월요일 시작 주
const weekStartOf = (d) => { const s = startOfDay(d); return addDays(s, -((s.getDay() + 6) % 7)); };
const monthStartOf = (d) => new Date(d.getFullYear(), d.getMonth(), 1);
const md = (d) => `${d.getMonth() + 1}/${d.getDate()}`;

// 치과명 축약 — 접미사(치과의원 등)만 떼고 나머지는 CSS 말줄임에 맡긴다. 떼고 비면 원문 유지.
const shortClinic = (name) => {
    const s = (name || '').trim();
    const cut = s.replace(/(치과의원|치과병원|치과)$/, '').trim();
    return cut || s;
};

/**
 * @param {object} props
 *  mode            'lab' | 'clinic' — clinic은 열람만(쓰기 버튼 없음)
 *  connError       거래처 목록 조회 실패 사유(기공소) — 있으면 접수 행이 비어 있음을 상단에 알린다(조용한 실패 금지)
 *  rows            진행 중 웹 의뢰서 행 [{ id, patientName, _col('intake'|'confirm'|'design'|'packed' — 치과 모드는 'mill'), _due(Date|null), _badgeLabel?, ... }]
 *  doneRows        완료(done) 웹 의뢰서 행 (_col 'done') — [완료 포함] 체크 시만 표시
 *  pastDueRows     지난 납기 미완료 행 (보드 pastDueRows 규칙 재사용) — 상단 띠 개수·펼침 목록
 *  columns         보드 COLUMNS([[key,label],...]) — 배지 라벨 원본
 *  partnerNameOf   (row) => 상대 업체명
 *  (멈춤 배지·정렬은 캘린더에서 쓰지 않는다 — 2026-09-29 대표 지시. 보드·목록의 멈춤 배지는 그대로)
 *  checkVal        (row, field) => boolean (밀링 체크 낙관값 포함)
 *  millChecks      [['millChecked','가공'],['printChecked','3D']]
 *  nextColOf/prevColOf/nextLabelOf  보드 단계 이동 표 (밀링완료의 다음은 없음 — 버튼 미표시)
 *  revertBlocked(row, prevCol)/revertBlockedMsg  밀링 기록이 있어 되돌리기 불가한 행 — 버튼 대신 문구 (2026-10-12 검토 P2-1)
 *  movingId                         쓰기 진행 중 표시
 *  onOpen(row) / onMoveStage(row, col) / onIntakeToConfirm(row) / onToggleCheck(row, field)
 *  onPreview3d(row)   디자인 완료 3D 파일(designPreview.files) 확인 — 있을 때만 줄·팝오버에 [3D] 버튼 (2026-09-29)
 *  escDisabled        3D 뷰어가 위에 열려 있으면 true — 모달 Esc 무시
 */
function ProductionCalendar({
    mode, connError, rows, doneRows, pastDueRows, columns,
    partnerNameOf, checkVal, millChecks,
    nextColOf, prevColOf, nextLabelOf,
    revertBlocked, revertBlockedMsg,
    movingId,
    onOpen, onMoveStage, onIntakeToConfirm, onToggleCheck,
    onPreview3d,
    escDisabled = false,   // 3D 뷰어(StlViewer)가 열려 있으면 그날 환자 모달의 Esc 무시 (2026-09-29 검토 P2-2)
}) {
    // ── 상태 (훅은 전부 최상단 — 조기 반환 없음) ──
    const [range, setRange] = useState(() => {          // 'week' | 'month' (localStorage 기억)
        // 기본 = 월간 (대표 지시 2026-09-30, 종전 주간). 키를 prodCalRange2로 바꿔 'week'를 저장해 둔 사람도 새 기본을 한 번은 본다.
        try { return localStorage.getItem('prodCalRange2') === 'week' ? 'week' : 'month'; } catch (e) { return 'month'; }
    });
    const setRangePersist = (v) => {
        setRange(v);
        try { localStorage.setItem('prodCalRange2', v); } catch (e) { console.warn('[ProductionCalendar] 보기 범위 저장 실패(사파리 프라이빗 등) — 기억만 포기', e); }
    };
    const [anchor, setAnchor] = useState(() => startOfDay(new Date()));   // 보고 있는 주/월의 기준일
    const [includeDone, setIncludeDone] = useState(true);                // [완료 포함] — 기본 켬 (대표 지시 2026-09-30, 종전 숨김)
    const [showPast, setShowPast] = useState(false);                     // 상단 띠 펼침
    const [pickedDay, setPickedDay] = useState(null);                    // 그날 환자 모달이 열린 날짜 키(dayKey) — 줄·칸·헤더 클릭 공통
    const [isMobile, setIsMobile] = useState(() => (typeof window !== 'undefined' ? window.innerWidth <= MOBILE_MAX_PX : false));
    const rootRef = useRef(null);

    useEffect(() => {
        const onResize = () => setIsMobile(window.innerWidth <= MOBILE_MAX_PX);
        window.addEventListener('resize', onResize);
        return () => window.removeEventListener('resize', onResize);
    }, []);
    // (구) 줄 팝오버는 그날 환자 모달(ProcessDayModal)로 통합 — Esc·바깥 클릭·스크롤 잠금은 모달이 맡는다 (2026-09-29)

    const today = startOfDay(new Date());
    const todayMs = today.getTime();
    const colLabel = useMemo(() => {
        const m = { intake: '접수', done: '완료' };   // 접수 칸은 '접수'로 짧게 (기획서 §2-2), 나머지는 보드 COLUMNS 라벨 그대로
        (columns || []).forEach(([k, lb]) => { if (k !== 'intake') m[k] = lb; });
        return m;
    }, [columns]);

    // ── 표시 행 = 진행 중 + (완료 포함 시) 완료. 납기 없는 건은 올리지 않고 개수만 센다 ──
    const allRows = useMemo(() => (includeDone ? [...(rows || []), ...(doneRows || [])] : (rows || [])), [rows, doneRows, includeDone]);
    const noDueCount = useMemo(() => (rows || []).filter(o => !o._due).length, [rows]);
    const colRank = (c) => { const i = COL_ORDER.indexOf(c); return i < 0 ? COL_ORDER.length : i; };
    // 셀 안 순서: 단계 순(접수→확인→디자인 완료→(치과 모드 밀링완료)→포장 완료→완료) → 환자명 (기획서 §2-4; 멈춤 우선 정렬은 2026-09-29 제거)
    const sortCell = (list) => list.sort((a, b) => {
        const cr = colRank(a._col) - colRank(b._col);
        if (cr !== 0) return cr;
        return (a.patientName || '').localeCompare(b.patientName || '', 'ko');
    });
    const byDay = useMemo(() => {
        const m = new Map();
        allRows.forEach(o => {
            if (!o._due) return;
            const k = dayKey(o._due);
            if (!m.has(k)) m.set(k, []);
            m.get(k).push(o);
        });
        for (const [, list] of m) sortCell(list);
        return m;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [allRows]);

    // ── 기간 계산 ──
    const weekStart = weekStartOf(anchor);
    const weekDays = Array.from({ length: 7 }, (_, i) => addDays(weekStart, i));
    const monthStart = monthStartOf(anchor);
    const monthGridStart = weekStartOf(monthStart);
    const monthCells = Array.from({ length: 42 }, (_, i) => addDays(monthGridStart, i));   // 6주 고정 격자
    const move = (dir) => {
        if (range === 'week') setAnchor(a => addDays(a, 7 * dir));
        else setAnchor(a => { const n = new Date(a.getFullYear(), a.getMonth() + dir, 1); return n; });
        setPickedDay(null);
    };
    const goToday = () => { setAnchor(startOfDay(new Date())); setPickedDay(null); };
    const rangeTitle = range === 'week'
        ? `${md(weekDays[0])} – ${md(weekDays[6])}`
        : `${monthStart.getFullYear()}년 ${monthStart.getMonth() + 1}월`;
    const isTodayWeek = weekStart.getTime() === weekStartOf(today).getTime();

    // ── 스타일 (ProductionBoard S 톤) ──
    const S = {
        bar: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, flexWrap: 'wrap', marginBottom: 10 },
        navGroup: { display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' },
        btn: { border: `1px solid ${C.border}`, background: C.surface, borderRadius: 8, fontSize: 12, padding: '5px 10px', color: C.textSecondary, fontFamily: 'inherit', cursor: 'pointer', fontWeight: 700 },
        btnOn: { border: '1px solid rgba(99,102,241,0.35)', background: '#eef2ff', color: C.cat[0] },
        title: { fontSize: 13.5, fontWeight: 700, color: C.textPrimary, minWidth: 110, textAlign: 'center' },
        checkLabel: { display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 12, color: C.textSecondary, cursor: 'pointer', fontWeight: 600 },
        checkBox: { margin: 0, width: 13, height: 13, cursor: 'pointer', accentColor: C.cat[0] },
        pastBar: { width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, background: '#fdf3f3', border: '1px solid rgba(208,59,59,0.30)', borderRadius: 8, padding: '8px 12px', fontSize: 12.5, fontWeight: 700, color: C.critical, cursor: 'pointer', fontFamily: 'inherit', textAlign: 'left', marginBottom: 10 },
        pastBarZero: { background: C.surface, border: `1px solid ${C.border}`, color: C.muted, cursor: 'default' },
        noDue: { fontSize: 12, color: C.muted, marginBottom: 10 },
        grid: { display: 'grid', gridTemplateColumns: 'repeat(7, minmax(0, 1fr))', gap: 6 },
        dayHead: (isToday, isPastRed) => ({
            cursor: 'pointer',
            fontSize: 12, fontWeight: 700, padding: '4px 6px', borderBottom: `2px solid ${isToday ? C.cat[0] : C.border}`,
            color: isPastRed ? C.critical : (isToday ? C.cat[0] : C.textSecondary), marginBottom: 6, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 4,
        }),
        dayCol: (isToday) => ({ background: isToday ? '#f5f6ff' : C.surface, border: `1px solid ${isToday ? 'rgba(99,102,241,0.35)' : C.border}`, borderRadius: 8, padding: '6px 6px 8px', minHeight: 120, minWidth: 0 }),
        count: { color: C.muted, fontWeight: 600, fontSize: 11 },
        cellList: { display: 'flex', flexDirection: 'column', gap: 4 },
        line: (isDone) => ({ position: 'relative', display: 'flex', alignItems: 'center', gap: 4, fontSize: 12, padding: '3px 5px', borderRadius: 6, cursor: 'pointer', color: isDone ? C.muted : C.textPrimary, background: isDone ? '#f8fafc' : 'transparent', minWidth: 0 }),
        lineHover: { background: '#eef2ff' },
        dot: { width: 6, height: 6, borderRadius: '50%', background: C.critical, flexShrink: 0 },
        name: (isDone) => ({ fontWeight: 700, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', minWidth: 0, textDecoration: isDone ? 'line-through' : 'none' }),
        clinic: { color: C.muted, fontSize: 11, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', minWidth: 0, flexShrink: 1 },
        badge: (col) => { const c = COL_STYLE[col] || COL_STYLE.done; return { display: 'inline-flex', alignItems: 'center', flexShrink: 0, fontSize: 9.5, fontWeight: 700, color: c.fg, background: c.bg, border: `1px solid ${c.bd}`, borderRadius: 5, padding: '1px 5px', whiteSpace: 'nowrap' }; },
        empty: { fontSize: 11.5, color: '#9ca3af', textAlign: 'center', padding: '10px 4px' },
        // 모달 카드 안 조작 버튼(단계 이동·밀링 체크)
        popRow: { display: 'flex', gap: 5, marginTop: 8, flexWrap: 'wrap' },
        pbtn: { border: `1px solid ${C.border}`, background: C.plane, borderRadius: 6, fontSize: 11, padding: '4px 8px', cursor: 'pointer', color: C.textSecondary, fontFamily: 'inherit', fontWeight: 600 },
        pbtnPrimary: { border: '1px solid rgba(99,102,241,0.35)', background: '#eef2ff', borderRadius: 6, fontSize: 11, padding: '4px 8px', cursor: 'pointer', color: C.cat[0], fontFamily: 'inherit', fontWeight: 700 },
        // 월간
        mCell: (isToday, inMonth) => ({ background: isToday ? '#f5f6ff' : (inMonth ? C.surface : C.plane), border: `1px solid ${isToday ? 'rgba(99,102,241,0.35)' : C.border}`, borderRadius: 8, padding: '5px 6px', minHeight: 104, minWidth: 0, cursor: 'pointer', opacity: inMonth ? 1 : 0.55 }),
        mDate: (isToday, isPastRed) => ({ fontSize: 12, fontWeight: 700, color: isPastRed ? C.critical : (isToday ? C.cat[0] : C.textSecondary), display: 'flex', alignItems: 'center', justifyContent: 'space-between' }),
        mCount: { display: 'inline-flex', alignItems: 'center', justifyContent: 'center', minWidth: 18, height: 18, borderRadius: 9, fontSize: 10.5, fontWeight: 700, color: '#fff', background: C.cat[0], padding: '0 5px' },
        mList: { display: 'flex', flexDirection: 'column', gap: 2, marginTop: 3 },
        mMore: { fontSize: 10.5, color: C.muted, marginTop: 2 },
        btn3d: { flexShrink: 0, border: '1px solid rgba(99,102,241,0.35)', background: '#eef2ff', color: C.cat[0], borderRadius: 5, fontSize: 9.5, fontWeight: 800, padding: '1px 5px', lineHeight: '14px', cursor: 'pointer', fontFamily: 'inherit' },
        // 폰: 날짜 헤더 + 세로 목록
        mobDay: (isToday) => ({ background: isToday ? '#f5f6ff' : C.surface, border: `1px solid ${isToday ? 'rgba(99,102,241,0.35)' : C.border}`, borderRadius: 8, padding: '6px 8px 8px', marginBottom: 6 }),
    };

    // ── 한 건 표시 줄 — 누르면 그날 환자 모달(팝오버 통합, 2026-09-29 대표 지시). 멈춤 배지(⚠N일)는 캘린더에 표시하지 않는다(보드·목록만) ──
    const isPastUnfinished = (o) => o._col !== 'done' && !!o._due && o._due.getTime() < todayMs;
    const openDayOf = (o) => { if (o._due) setPickedDay(dayKey(o._due)); };
    // 배지 문구: 보드가 행별로 준 _badgeLabel(종결 행 — 기공소 stage3Of / 치과 clinicStageOf 라벨)이 우선, 접수 행은 배지 없음(2026-10-12 치과 모드도)
    const badgeLabelOf = (o) => {
        if (o._badgeLabel !== undefined) return o._badgeLabel;
        if (o._col === 'intake') return '';
        return colLabel[o._col] || o._col;
    };
    const renderLine = (o, keyPrefix = '') => {
        const key = keyPrefix + o.id;
        const isDone = o._col === 'done';
        const badgeText = badgeLabelOf(o);
        const clinic = partnerNameOf ? partnerNameOf(o) : '';
        const has3d = !!onPreview3d && (o.designPreview?.files || []).length > 0;
        return (
            <div key={key} style={S.line(isDone)}
                onClick={(e) => { e.stopPropagation(); openDayOf(o); }}
                title={`${o.patientName || '(환자명 없음)'} · ${clinic} — 누르면 이 날 환자 카드`}>
                {isPastUnfinished(o) && <span style={S.dot} />}
                <span style={S.name(isDone)}>{o.patientName || '(환자명 없음)'}</span>
                <span style={S.clinic}>{'· ' + shortClinic(clinic)}</span>
                {badgeText && <span style={S.badge(o._col)}>{badgeText}</span>}
                {has3d && (
                    <button style={S.btn3d} title="3D 디자인 확인" onClick={(e) => { e.stopPropagation(); onPreview3d(o); }}>{'3D'}</button>
                )}
            </div>
        );
    };

    // ── 그날 환자 모달 (ProcessDayModal 공용) — 기공소는 카드 안에 기존 팝오버의 단계 이동 버튼·밀링 체크를 그대로 둔다(핸들러 재사용) ──
    const dateOfKey = (k) => { const [y, m, d] = k.split('-').map(Number); return new Date(y, m - 1, d); };
    // 카드 공정 세부 문구 — 저장값 그대로(보드 workDetailEl과 같은 규칙). 지어내지 않는다.
    const detailOf = (o) => {
        if (o._detail !== undefined) return o._detail;   // 보드가 준 상세(치과 모드 종결 행 — clinicStageOf detail, 2026-10-12)
        if (o._col === 'done') return o.stageSource === 'auto_due' ? '납기일 자정 자동 처리 — 납품·수령을 증명하지 않습니다' : null;
        if (o._col === 'confirm') return o.processStage === 'designing' ? '디자인 시작' : o.processStage === 'scanning' ? '스캔 감지' : o.processStage === 'received' ? '의뢰서 열람' : null;
        // (구) 'mill' 열 '밀링 감지' 문구 — 2026-10-12 치과 모드도 3단계라 밀링은 「디자인 완료」로 묶여 따로 말하지 않는다
        if (o._col === 'intake') return mode === 'lab' ? '아직 열어 보지 않은 의뢰서' : '기공소 확인 전';
        return null;
    };
    const pickedDate = pickedDay ? dateOfKey(pickedDay) : null;
    const pickedEntries = (pickedDay ? (byDay.get(pickedDay) || []) : []).map(o => ({
        key: o.id, o,
        st: { label: badgeLabelOf(o), detail: detailOf(o) },
        color: (COL_STYLE[o._col] || COL_STYLE.done).fg,
        done: o._col === 'done',
    }));
    const actionsOf = (e) => {
        const o = e.o;
        if (mode !== 'lab' || e.done) return null;
        const busy = movingId === o.id;
        const nextCol = nextColOf ? nextColOf[o._col] : null;
        const prevCol = prevColOf ? prevColOf[o._col] : null;
        const hasChecks = CHECK_COLS.includes(o._col) && Array.isArray(millChecks);
        const hasMove = o._col === 'intake' || (o._col !== 'intake' && (prevCol || nextCol));
        if (!hasChecks && !hasMove) return null;
        return (
            <>
                {hasChecks && (
                    <div style={{ display: 'flex', gap: 10 }}>
                        {millChecks.map(([f, lb]) => {
                            const on = checkVal ? checkVal(o, f) : o[f] === true;
                            return (
                                <label key={f} style={{ ...S.checkLabel, fontSize: 11 }}>
                                    <input type="checkbox" checked={on} style={S.checkBox} onChange={() => onToggleCheck && onToggleCheck(o, f)} />
                                    {lb}
                                </label>
                            );
                        })}
                    </div>
                )}
                <div style={{ ...S.popRow, marginTop: hasChecks ? 8 : 0 }}>
                    {o._col === 'intake' && (
                        <button style={S.pbtnPrimary} disabled={busy} onClick={() => onIntakeToConfirm && onIntakeToConfirm(o)}>{'▸ 의뢰서 확인으로'}</button>
                    )}
                    {o._col !== 'intake' && prevCol && !(revertBlocked && revertBlocked(o, prevCol)) && (
                        <button style={S.pbtn} disabled={busy} onClick={() => onMoveStage && onMoveStage(o, prevCol)}>{'◂ 이전 단계'}</button>
                    )}
                    {o._col !== 'intake' && prevCol && revertBlocked && revertBlocked(o, prevCol) && (
                        <span style={{ fontSize: 11, color: C.muted, alignSelf: 'center' }}>{revertBlockedMsg || ''}</span>
                    )}
                    {o._col !== 'intake' && nextCol && (
                        <button style={S.pbtnPrimary} disabled={busy} onClick={() => onMoveStage && onMoveStage(o, nextCol)}>{(nextLabelOf && nextLabelOf[o._col]) || '다음 ▸'}</button>
                    )}
                </div>
            </>
        );
    };

    // ── 주간 (데스크톱 7열 / 폰 세로 목록) ──
    const renderWeek = () => {
        if (isMobile) {
            return (
                <div>
                    {weekDays.map((d, i) => {
                        const list = byDay.get(dayKey(d)) || [];
                        const isToday = d.getTime() === todayMs;
                        const pastRed = d.getTime() < todayMs && list.some(isPastUnfinished);
                        return (
                            <div key={dayKey(d)} style={S.mobDay(isToday)}>
                                <div style={S.dayHead(isToday, pastRed)} onClick={() => setPickedDay(dayKey(d))} title="이 날 환자 카드 보기">
                                    <span>{`${WEEKDAY[i]} ${md(d)}${isToday ? ' (오늘)' : ''}`}</span>
                                    <span style={S.count}>{list.length ? `${list.length}건` : ''}</span>
                                </div>
                                <div style={S.cellList}>
                                    {list.length === 0 ? <div style={S.empty}>{'없음'}</div> : list.map(o => renderLine(o))}
                                </div>
                            </div>
                        );
                    })}
                </div>
            );
        }
        return (
            <div style={S.grid}>
                {weekDays.map((d, i) => {
                    const list = byDay.get(dayKey(d)) || [];
                    const isToday = d.getTime() === todayMs;
                    const pastRed = d.getTime() < todayMs && list.some(isPastUnfinished);
                    return (
                        <div key={dayKey(d)} style={S.dayCol(isToday)}>
                            <div style={S.dayHead(isToday, pastRed)} onClick={() => setPickedDay(dayKey(d))} title="이 날 환자 카드 보기">
                                <span>{`${WEEKDAY[i]} ${md(d)}${isToday ? ' · 오늘' : ''}`}</span>
                                <span style={S.count}>{list.length || ''}</span>
                            </div>
                            <div style={S.cellList}>
                                {list.length === 0 ? <div style={S.empty}>{'—'}</div> : list.map(o => renderLine(o))}
                            </div>
                        </div>
                    );
                })}
            </div>
        );
    };

    // ── 월간 — 날짜당 개수 배지 + 상위 N줄(주간 셀과 같은 줄 형식: 환자명 · 치과명 · 공정 배지 · 3D), 칸 클릭 = 그날 환자 모달(종전 '주간으로 이동' 대체, 2026-09-29) ──
    const MONTH_LINES = 3;
    const renderMonth = () => (
        <div>
            <div style={{ ...S.grid, marginBottom: 4 }}>
                {WEEKDAY.map(w => <div key={w} style={{ fontSize: 11.5, fontWeight: 700, color: C.muted, textAlign: 'center' }}>{w}</div>)}
            </div>
            <div style={S.grid}>
                {monthCells.map((d, i) => {
                    const list = byDay.get(dayKey(d)) || [];
                    const isToday = d.getTime() === todayMs;
                    const inMonth = d.getMonth() === monthStart.getMonth();
                    const pastRed = d.getTime() < todayMs && list.some(isPastUnfinished);
                    return (
                        <div key={dayKey(d)} style={S.mCell(isToday, inMonth)}
                            onClick={() => setPickedDay(dayKey(d))}
                            title="이 날 환자 카드 보기">
                            <div style={S.mDate(isToday, pastRed)}>
                                <span>{d.getDate()}</span>
                                {list.length > 0 && <span style={S.mCount}>{list.length}</span>}
                            </div>
                            {list.length > 0 && (
                                <div style={S.mList}>
                                    {list.slice(0, MONTH_LINES).map(o => renderLine(o, 'm_'))}
                                </div>
                            )}
                            {list.length > MONTH_LINES && <div style={S.mMore}>{`+${list.length - MONTH_LINES}건`}</div>}
                        </div>
                    );
                })}
            </div>
        </div>
    );

    const pastList = pastDueRows || [];
    const pastSorted = useMemo(() => sortCell([...pastList].filter(o => !!o._due)).sort((a, b) => a._due.getTime() - b._due.getTime()), [pastList]);   // eslint-disable-line react-hooks/exhaustive-deps

    return (
        <div ref={rootRef}>
            {/* ── 거래처 목록 조회 실패 — 접수 행이 비어 있음을 알린다(조용한 실패 금지) ── */}
            {connError && (
                <div style={{ ...S.pastBar, cursor: 'default' }}>
                    <span>{'접수 의뢰서를 캘린더에 올리지 못했습니다'}</span>
                    <span style={{ color: C.muted, fontWeight: 600 }}>{'거래처 목록 조회 실패: ' + connError}</span>
                </div>
            )}
            {/* ── 이동·기간·완료 포함 ── */}
            <div style={S.bar}>
                <div style={S.navGroup}>
                    <button style={S.btn} onClick={() => move(-1)} title={range === 'week' ? '지난 주' : '지난 달'}>{'<'}</button>
                    <button style={{ ...S.btn, ...((range === 'week' ? isTodayWeek : (monthStart.getTime() === monthStartOf(today).getTime())) ? S.btnOn : {}) }} onClick={goToday}>
                        {range === 'week' ? '이번 주' : '이번 달'}
                    </button>
                    <button style={S.btn} onClick={() => move(1)} title={range === 'week' ? '다음 주' : '다음 달'}>{'>'}</button>
                    <span style={S.title}>{rangeTitle}</span>
                </div>
                <div style={S.navGroup}>
                    <button style={{ ...S.btn, ...(range === 'week' ? S.btnOn : {}) }} onClick={() => { setRangePersist('week'); setPickedDay(null); }}>{'주'}</button>
                    <button style={{ ...S.btn, ...(range === 'month' ? S.btnOn : {}) }} onClick={() => { setRangePersist('month'); setPickedDay(null); }}>{'월'}</button>
                    <label style={{ ...S.checkLabel, marginLeft: 6 }}>
                        <input type="checkbox" checked={includeDone} style={S.checkBox} onChange={() => { setIncludeDone(v => !v); setPickedDay(null); }} />
                        {'완료 포함'}
                    </label>
                </div>
            </div>

            {/* ── 지난 납기 미완료 띠 (항상 표시 — 과거 날짜에 묻히지 않게, 기획서 문제점 7) ── */}
            <button
                style={{ ...S.pastBar, ...(pastSorted.length === 0 ? S.pastBarZero : {}) }}
                onClick={() => { if (pastSorted.length) setShowPast(v => !v); }}
                disabled={pastSorted.length === 0}>
                <span>{(pastSorted.length ? (showPast ? '▾ ' : '▸ ') : '') + '지난 납기 미완료 ' + pastSorted.length + '건'}</span>
                <span style={{ color: C.muted, fontWeight: 600 }}>{'납기일이 지났는데 아직 완료 처리되지 않은 웹 의뢰서 (서버가 납기일 자정에 자동 완료 처리합니다)'}</span>
            </button>
            {showPast && pastSorted.length > 0 && (
                <div style={{ ...S.cellList, background: C.surface, border: `1px solid ${C.border}`, borderRadius: 8, padding: '6px 8px', marginBottom: 10 }}>
                    {pastSorted.map(o => (
                        <div key={'p_' + o.id} style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
                            <span style={{ fontSize: 11.5, fontWeight: 700, color: C.critical, flexShrink: 0, width: 44 }}>{md(o._due)}</span>
                            <div style={{ flex: 1, minWidth: 0 }}>{renderLine(o, 'p_')}</div>
                        </div>
                    ))}
                </div>
            )}

            {/* ── 납기 없는 의뢰서 (있을 때만 개수) ── */}
            {noDueCount > 0 && (
                <div style={S.noDue}>{`납기 없는 의뢰서 ${noDueCount}건 — 캘린더에는 표시되지 않습니다(보드·목록에는 있습니다).`}</div>
            )}

            {range === 'week' ? renderWeek() : renderMonth()}

            <div style={{ marginTop: 8, fontSize: 11.5, color: C.muted }}>
                {mode === 'lab'
                    ? '날짜 = 납기일(치과에 기공물이 도착해야 하는 날). 웹 의뢰서만 표시하며 덴클 프로그램 수집 케이스는 보드·목록에서 봅니다. 날짜나 건을 누르면 그날 환자 카드가 열립니다(의뢰서 열기·단계 이동·밀링 체크).'
                    : '날짜 = 납기일(기공물이 치과에 도착해야 하는 날). 날짜나 건을 누르면 그날 환자 카드가 열립니다.'}
            </div>

            {/* 그날 환자 모달 — 치과·기공소 공용 ProcessDayModal. 쓰기는 actionsOf 안의 기존 핸들러(onMoveStage·onIntakeToConfirm·onToggleCheck)만 */}
            <ProcessDayModal
                date={pickedDate}
                entries={pickedEntries}
                partnerNameOf={partnerNameOf}
                dueLabel="납기"
                onClose={() => setPickedDay(null)}
                onOpen={(o) => { setPickedDay(null); onOpen && onOpen(o); }}
                onPreview={onPreview3d}
                escDisabled={escDisabled}
                hideDesignChip   // 2026-10-12 치과 모드도 3단계 — 보조 '디자인완료' 칩은 공정 행 라벨과 겹쳐 양쪽 모두 숨김
                actionsOf={actionsOf}
                foot={mode === 'clinic' ? '단계 이동은 기공소가 합니다.' : null}
            />
        </div>
    );
}

export default ProductionCalendar;
